import Table from 'cli-table3';
import PrintableResult from './PrintableResult';
import { receiptLogoLine } from './receiptLogo';
import { diContainer } from '../DIContainer';
export default class PrintableText {
    printableContent;
    charsPerLine;
    allowDoubleFontWidth;
    constructor(printableContent, charsPerLine = 32) {
        const Device = diContainer.resolve('device');
        this.printableContent = printableContent;
        this.charsPerLine = charsPerLine;
        this.allowDoubleFontWidth = Device.get()?.platform === 'electron' && charsPerLine >= 40;
    }
    /**
     * Processes the printable content and generates formatted text.
     *
     * The method processes the printable content based on its type ('S', 'B', 'D', 'L', 'T')
     * and calls the corresponding process function.
     *
     * @returns PrintableResult
     */
    process() {
        const result = this.printableContent.map(c => {
            if (c.type === 'L') {
                const content = c.data[0].content[0] || '';
                const align = c.data[0].align || 'L';
                const scale = c.data[0].scale || '1,1';
                const style = c.data[0].style || null;
                return this.processLine(content, align, scale, style);
            }
            else if (c.type === 'S') {
                const scale = c.data[0].scale || '1,1';
                const style = c.data[0].style || null;
                return this.processSpaceBetween(c.data[0].content, scale, style);
            }
            else if (c.type === 'B') {
                const scale = c.data[0].scale || '1,1';
                const style = c.data[0].style || null;
                return this.processSpaceOrBreak(c.data[0].content, scale, style);
            }
            else if (c.type === 'D') {
                const delimiter = c.data[0].delimiter || ' ';
                const content = c.data[0].content[0] || '';
                const align = c.data[0].align || 'L';
                const scale = c.data[0].scale || '1,1';
                return this.processDivider(delimiter, content, align, scale);
            }
            else if (c.type === 'T') {
                const content = c.data || [];
                const colNumber = c.colNumber || 2;
                const colWidths = c.text?.colWidths || [];
                return this.processTable(content, colNumber, colWidths);
            }
            else if (c.type === 'QR') {
                const content = c.data[0].content[0] || '';
                const size = c.data[0].size;
                return this.processQrCode(content, size);
            }
            else if (c.type === 'LOGO') {
                return this.processLogo(c.data[0].content[0] || '');
            }
            else {
                return null;
            }
        }).filter((c) => c !== null);
        const text = result.flatMap(r => r?.getLines() || []);
        const highlight = result.flatMap(r => r?.highlight || []);
        return new PrintableResult(text, false, highlight);
    }
    /**
     * Process the input data and create a formatted table as string.
     *
     * @param data The data to be inserted into the table. It should be an array of IPrintableContentData, each containing column data and options.
     * @param colNumber The number of columns in the table.
     * @param colWidths An optional array specifying the width of each column. If not provided, column widths will be calculated automatically.
     * @returns An array of string where each string is a row in the table.
     */
    processTable(data, colNumber, colWidths = []) {
        colWidths = this.calculateColWidths(data, colNumber, colWidths);
        const table = new Table({
            chars: {
                'top': '',
                'top-mid': '',
                'top-left': '',
                'top-right': '',
                'bottom': '',
                'bottom-mid': '',
                'bottom-left': '',
                'bottom-right': '',
                'left': '',
                'left-mid': '',
                'mid': '',
                'mid-mid': '',
                'right': '',
                'right-mid': '',
                'middle': ' '
            },
            colWidths,
            wordWrap: true,
            style: { 'padding-left': 0, 'padding-right': 0 },
        });
        const cols = JSON.parse(JSON.stringify(data)).map((col) => {
            return {
                content: col.content.join(''),
                colSpan: col.colSpan,
                hAlign: col.align === 'C' ? 'center' : (col.align === 'R' ? 'right' : 'left')
            };
        });
        let rows = [];
        let curRow = [];
        cols.forEach((col, index) => {
            if (col.content === 'divider') {
                if (index === 0)
                    return;
                rows.push([...curRow]);
                rows.push([{ content: '-'.repeat(this.charsPerLine), hAlign: 'left', colSpan: colNumber }]);
                curRow = [];
                return;
            }
            const cellsUsed = curRow.reduce((a, b) => a + (b.colSpan || 1), 0);
            if ((cellsUsed + (col.colSpan || 1) <= colNumber)) {
                const isLastColumn = cellsUsed + (col.colSpan || 1) === colNumber;
                if (isLastColumn && col.content.length > colWidths[colWidths.length - 1]) {
                    rows.push([...curRow]);
                    col.colSpan = colNumber;
                    col.hAlign = 'right';
                    curRow = [col];
                }
                else {
                    curRow.push(col);
                }
            }
            else {
                rows.push([...curRow]);
                curRow = [col];
            }
            if (index === cols.length - 1) {
                rows.push([...curRow]);
            }
        });
        table.push(...rows);
        const lines = table.toString().split('\n');
        return new PrintableResult(lines, false, new Array(lines.length).fill({
            width: 'normal',
            height: 'normal',
            bold: false
        }));
    }
    /**
     * Formats the line according to the alignment
     *
     * @param content Content of the line
     * @param align The alignment of the text content ('L', 'R', or 'C')
     * @param scale Scale of the line: width,height. Default is '1,1'
     * @returns PrintableResult
     */
    processLine(content, align = 'L', scale = '1,1', style = null) {
        const doubleWidth = this.allowDoubleFontWidth && scale?.split(',')[0] === '2';
        const charsPerLine = doubleWidth ? Math.floor(this.charsPerLine / 2) : this.charsPerLine;
        let lines = [];
        // Break the content into chunks that fit within the charsPerLine limit
        let contentChunks = this.splitString(content, charsPerLine);
        for (let i = 0; i < contentChunks.length; i++) {
            let contentChunk = contentChunks[i];
            let line = '';
            let leftPadding, rightPadding;
            // Pad against the (possibly halved) charsPerLine so double-width lines
            // get half the whitespace and stay on a single physical line.
            switch (align) {
                case 'L':
                    leftPadding = '';
                    rightPadding = ' '.repeat(charsPerLine - contentChunk.length);
                    break;
                case 'R':
                    leftPadding = ' '.repeat(charsPerLine - contentChunk.length);
                    rightPadding = '';
                    break;
                case 'C':
                    leftPadding = ' '.repeat(Math.floor((charsPerLine - contentChunk.length) / 2));
                    rightPadding = ' '.repeat(charsPerLine - contentChunk.length - leftPadding.length);
                    break;
                default:
                    throw new Error(`Invalid alignment value: ${align}. Expected 'L', 'R', or 'C'.`);
            }
            line = leftPadding + contentChunk + rightPadding;
            lines.push(line);
        }
        return new PrintableResult(lines, false, new Array(lines.length).fill({
            width: doubleWidth ? 'large' : 'normal',
            height: scale.split(',')[1] === '2' ? 'large' : 'normal',
            bold: style?.fontWeight && parseFloat(style.fontWeight) > 400 ? true : false
        }));
    }
    /**
     * Formats an array of two strings according to 'space-between' CSS rule
     *
     * @param arr Array of two strings
     * @param scale Scale of the line: width,height. Default is '1,1'
     * @returns PrintableResult
     */
    processSpaceBetween(arr, scale = '1,1', style = null) {
        const doubleWidth = this.allowDoubleFontWidth && scale?.split(',')[0] === '2';
        const charsPerLine = doubleWidth ? Math.floor(this.charsPerLine / 2) : this.charsPerLine;
        let lines = [];
        let [leftString, rightString] = arr;
        let availableSpace = charsPerLine - leftString.length - rightString.length;
        // Both strings can fit on the same line with at least one space
        if (availableSpace >= 1) {
            let line = leftString + ' '.repeat(availableSpace) + rightString;
            lines = [this.padLine(line, charsPerLine)];
        }
        else {
            // The strings can't fit on the same line, they need to be on separate lines
            let leftLines = this.splitString(leftString, charsPerLine);
            let rightLines = this.splitString(rightString, charsPerLine).map(line => ' '.repeat(charsPerLine - line.length) + line);
            lines = [...leftLines, ...rightLines];
        }
        return new PrintableResult(lines, false, new Array(lines.length).fill({
            width: doubleWidth ? 'large' : 'normal',
            height: scale.split(',')[1] === '2' ? 'large' : 'normal',
            bold: style?.fontWeight && parseFloat(style.fontWeight) > 400 ? true : false
        }));
    }
    /**
     * Formats an array of two strings according to 'space-or-break' rule
     *
     * @param arr Array of two strings
     * @param scale Scale of the line: width,height. Default is '1,1'
     * @returns PrintableResult
     */
    processSpaceOrBreak(arr, scale = '1,1', style = null) {
        const doubleWidth = this.allowDoubleFontWidth && scale?.split(',')[0] === '2';
        const charsPerLine = doubleWidth ? Math.floor(this.charsPerLine / 2) : this.charsPerLine;
        let lines = [];
        let [leftString, rightString] = arr;
        // Both strings can fit on the same line with a space
        if (leftString.length + rightString.length + 1 <= charsPerLine) {
            let line = leftString + ' ' + rightString;
            lines = [this.padLine(line, charsPerLine)];
        }
        else {
            // The strings can't fit on the same line, they need to be on separate lines
            let leftLines = this.splitString(leftString, charsPerLine).map(line => this.padLine(line, charsPerLine));
            let rightLines = this.splitString(rightString, charsPerLine).map(line => this.padLine(line, charsPerLine));
            lines = [...leftLines, ...rightLines];
        }
        return new PrintableResult(lines, false, new Array(lines.length).fill({
            width: doubleWidth ? 'large' : 'normal',
            height: scale.split(',')[1] === '2' ? 'large' : 'normal',
            bold: style?.fontWeight && parseFloat(style.fontWeight) > 400 ? true : false
        }));
    }
    /**
     * Creates a divider line with text content
     *
     * @param delimiter The character used as a divider
     * @param content The text content
     * @param align The alignment of the text content ('L', 'R', or 'C')
     * @param scale Scale of the line: width,height. Default is '1,1'
     * @returns PrintableResult
     */
    processDivider(delimiter, content, align, scale = '1,1') {
        const doubleWidth = this.allowDoubleFontWidth && scale?.split(',')[0] === '2';
        const charsPerLine = doubleWidth ? Math.floor(this.charsPerLine / 2) : this.charsPerLine;
        let lines = [];
        // Break the content into chunks that fit within the charsPerLine limit
        let contentChunks = this.splitString(content, charsPerLine);
        for (let i = 0; i < contentChunks.length; i++) {
            let contentChunk = contentChunks[i];
            let line = '';
            let leftPadding, rightPadding;
            switch (align) {
                case 'L':
                    leftPadding = '';
                    rightPadding = ' '.repeat(charsPerLine - contentChunk.length);
                    break;
                case 'R':
                    leftPadding = ' '.repeat(charsPerLine - contentChunk.length);
                    rightPadding = '';
                    break;
                case 'C':
                    leftPadding = ' '.repeat(Math.floor((charsPerLine - contentChunk.length) / 2));
                    rightPadding = ' '.repeat(charsPerLine - contentChunk.length - leftPadding.length);
                    break;
                default:
                    throw new Error(`Invalid alignment value: ${align}. Expected 'L', 'R', or 'C'.`);
            }
            line = leftPadding + contentChunk + rightPadding;
            // Only the last line should be padded with the delimiter character
            if (i === contentChunks.length - 1) {
                line = line.replace(/^ +| +$/g, '').padStart(leftPadding.length + contentChunk.length, delimiter).padEnd(charsPerLine, delimiter);
            }
            lines.push(line);
        }
        return new PrintableResult(lines, false, new Array(lines.length).fill({
            width: doubleWidth ? 'large' : 'normal',
            height: scale.split(',')[1] === '2' ? 'large' : 'normal',
            bold: false
        }));
    }
    /**
     * Creates a QR code text line markup
     *
     * @param content The text content
     * @param size Qr Code size
     * @returns PrintableResult
     */
    processQrCode(content, size) {
        let lines = [`<qrcode>${content}</qrcode>`];
        return new PrintableResult(lines, false, [{
                width: 'normal',
                height: 'normal',
                bold: false
            }]);
    }
    /**
     * The receipt logo's text lines: one empty line, then one unwrapped `<logo>…</logo>` line the
     * printer drivers turn into a bitmap (receiptLogo.ts). Nothing for a payload that is not base64.
     *
     * @param content base64 PNG
     * @returns PrintableResult | null
     */
    processLogo(content) {
        const line = receiptLogoLine(content);
        if (!line)
            return null;
        return new PrintableResult(['', line], false, [
            { width: 'normal', height: 'normal', bold: false },
            { width: 'normal', height: 'normal', bold: false },
        ]);
    }
    /**
     * Splits a string into lines of a given length, breaking at spaces where possible, and into individual characters where necessary
     *
     * @param str The string to be split
     * @param charsPerLine The maximum line length
     * @returns An array of lines
     */
    splitString(str, charsPerLine) {
        let words = str.split(' ');
        let lines = [];
        let line = '';
        words.forEach(word => {
            if (word.length > charsPerLine) {
                // Flush existing line before processing long word to avoid overflow
                if (line.trim()) {
                    lines.push(line.trim());
                    line = '';
                }
                // Word is too long, split it into segments
                let wordSegments = this.breakWord(word, charsPerLine);
                // Add all full-length segments to lines array
                let fullLengthSegments = wordSegments.filter(segment => segment.length === charsPerLine);
                lines = [...lines, ...fullLengthSegments];
                // Last segment, if any, is shorter and should be added to the line
                let lastSegment = wordSegments[wordSegments.length - 1];
                if (lastSegment.length < charsPerLine) {
                    line += lastSegment + ' ';
                }
                return;
            }
            if ((line + word).length > charsPerLine) {
                // Word doesn't fit on the current line, start a new line
                lines.push(line.trim());
                line = '';
            }
            // Word fits on the current line
            line += word + ' ';
        });
        // Add the remaining line, if any
        if (line)
            lines.push(line.trim());
        return lines;
    }
    /**
     * Breaks a long word into segments
     *
     * @param word The word to be broken into segments
     * @param charsPerLine The maximum line length
     * @returns An array of word segments
     */
    breakWord(word, charsPerLine) {
        let segments = [];
        while (word.length > charsPerLine) {
            let segment = word.substring(0, charsPerLine);
            segments.push(segment);
            word = word.substring(charsPerLine);
        }
        // If there's any remainder of the word, add it to the segments
        if (word)
            segments.push(word);
        return segments;
    }
    /**
     * Pads a line with spaces until it reaches a given length
     *
     * @param line The line to be padded
     * @param charsPerLine The maximum line length
     * @returns The padded line
     */
    padLine(line, charsPerLine) {
        return line + ' '.repeat(charsPerLine - line.length);
    }
    /**
     * Calculate and if needed adjust col widths
     *
     * @param {number[]} colWidths - Array of original column widths
     * @param {number[]} minimalColWidths - Array of minimal required column widths
     * @returns {number[]} - Distributed column widths
     */
    calculateColWidths(data, colNumber, colWidths = []) {
        // Calculate the width of each column
        const availableCharsPerLine = this.charsPerLine - colNumber + 1;
        if (colWidths?.length !== colNumber) {
            colWidths = Array(colNumber).fill(Math.floor(availableCharsPerLine / colNumber));
        }
        let totalFixedWidth = (colWidths.filter((w) => w !== null)).reduce((a, b) => a + b, 0);
        let expandableColIndex = colWidths.indexOf(null);
        if (expandableColIndex !== -1) {
            colWidths[expandableColIndex] = availableCharsPerLine - (totalFixedWidth || 0);
        }
        else if (totalFixedWidth && totalFixedWidth <= availableCharsPerLine) {
            colWidths[colWidths.length - 1] = (colWidths[colWidths.length - 1] || 0) + availableCharsPerLine - totalFixedWidth;
        }
        const columnsContents = this.transformCellsToColumns(data, colNumber);
        const minimalColWidths = columnsContents.map(colContents => Math.max(...(colContents.map(c => c.length))));
        colWidths = this.distributeColumnWidths(colWidths, minimalColWidths);
        const minChars = colWidths.filter(c => !!c).reduce((acc, cur) => acc += cur, 0);
        if (minChars < availableCharsPerLine) {
            colWidths[colWidths.length - 1] += availableCharsPerLine - minChars;
        }
        return colWidths;
    }
    /**
     * Distributes column widths based on minimal requirements and available space.
     *
     * @param {number[]} colWidths - Array of original column widths
     * @param {number[]} minimalColWidths - Array of minimal required column widths
     * @returns {number[]} - Distributed column widths
     */
    distributeColumnWidths(colWidths, minimalColWidths) {
        // Calculate total widths
        const totalOriginalWidth = colWidths.reduce((sum, width) => sum + width, 0);
        let totalMinimalWidth = minimalColWidths.reduce((sum, width) => sum + width, 0);
        // If total minimal width is greater to original width set last column min value to
        // whatever is specified as its width in colWidths
        if (totalMinimalWidth > totalOriginalWidth) {
            minimalColWidths[minimalColWidths.length - 1] = colWidths[colWidths.length - 1];
            totalMinimalWidth = minimalColWidths.reduce((sum, width) => sum + width, 0);
        }
        // If still over budget after clamping, return original targets — wordWrap handles overflow
        if (totalMinimalWidth > totalOriginalWidth) {
            return [...colWidths];
        }
        // If total minimal width is already equal to original width return it
        if (totalMinimalWidth === totalOriginalWidth) {
            return [...minimalColWidths];
        }
        // If all original widths are sufficient, return the original widths
        const allSufficient = colWidths.every((width, index) => width >= minimalColWidths[index]);
        if (allSufficient) {
            return [...colWidths];
        }
        // Calculate available width to distribute
        let availableWidth = totalOriginalWidth - totalMinimalWidth;
        // Start with minimal widths as the base
        const result = [...minimalColWidths];
        // Step 1: Identify columns that need to stay as they are
        // These are columns where minimalColWidth >= colWidth
        const fixedColumns = new Set();
        for (let i = 0; i < colWidths.length; i++) {
            if (minimalColWidths[i] >= colWidths[i]) {
                fixedColumns.add(i);
            }
        }
        // Step 2: Try to equalize remaining columns to the highest value
        let workingColumns = colWidths.map((_, index) => index).filter(index => !fixedColumns.has(index));
        while (workingColumns.length > 1) {
            // Find the highest value among working columns
            let highestIndex = workingColumns[0];
            let highestValue = result[highestIndex];
            for (const index of workingColumns) {
                if (result[index] > highestValue) {
                    highestIndex = index;
                    highestValue = result[index];
                }
            }
            // Try to bring all other columns to this height
            const columnsToEqualize = workingColumns.filter(index => index !== highestIndex);
            const widthNeeded = columnsToEqualize.reduce((sum, index) => sum + (highestValue - result[index]), 0);
            // If we have enough width, equalize all
            if (widthNeeded <= availableWidth) {
                for (const index of columnsToEqualize) {
                    const increase = highestValue - result[index];
                    result[index] = highestValue;
                    availableWidth -= increase;
                }
                break; // Done with step 2
            }
            else {
                // Not enough width, remove the highest and try again
                workingColumns = workingColumns.filter(index => index !== highestIndex);
                fixedColumns.add(highestIndex);
            }
        }
        // If we're down to just one column, give it all the remaining width
        if (workingColumns.length === 1) {
            result[workingColumns[0]] += availableWidth;
            availableWidth = 0;
        }
        // Step 3: If there are still width units to distribute, add them to the lowest columns
        if (availableWidth > 0) {
            // Find the lowest value among non-fixed columns
            const columnsToDistribute = colWidths.map((_, index) => index)
                .filter(index => !fixedColumns.has(index) && result[index] < colWidths[index]);
            if (columnsToDistribute.length > 0) {
                // Sort columns by their current width, ascending
                columnsToDistribute.sort((a, b) => result[a] - result[b]);
                // Find the lowest width
                const lowestWidth = result[columnsToDistribute[0]];
                // Find all columns with the lowest width
                const lowestColumns = columnsToDistribute.filter(index => result[index] === lowestWidth);
                // Distribute remaining width units one by one
                let colIndex = 0;
                while (availableWidth > 0 && lowestColumns.length > 0) {
                    const index = lowestColumns[colIndex % lowestColumns.length];
                    // Make sure we don't exceed the original width
                    if (result[index] < colWidths[index]) {
                        result[index]++;
                        availableWidth--;
                    }
                    else {
                        // Remove this column from distribution if it reaches original width
                        lowestColumns.splice(colIndex % lowestColumns.length, 1);
                        if (lowestColumns.length === 0)
                            break;
                    }
                    colIndex++;
                }
            }
        }
        return result;
    }
    /**
     * Process the input data and create an array of column contents
     *
     * @param data The data to be inserted into the table. It should be an array of IPrintableContentData, each containing column data and options.
     * @param colNumber The number of columns in the table.
     * @returns
     */
    transformCellsToColumns(data, colNumber) {
        // Initialize columns array with empty arrays
        const columns = Array(colNumber).fill(null).map(() => []);
        let currentRow = 0;
        let currentCol = 0;
        for (let i = 0; i < data.length; i++) {
            const cell = data[i];
            const content = (cell.content && cell.content[0]) ? cell.content.join('') : '';
            const colSpan = cell.colSpan || 1;
            // Handle colspan cells (typically row headers that span multiple columns)
            if (colSpan > 1) {
                // Skip this cell in our column representation
                // Move to the next row
                currentRow++;
                currentCol = 0;
                continue;
            }
            // Divider cells span the full row — skip and advance to next row
            if (content === 'divider') {
                currentCol = 0;
                currentRow++;
                continue;
            }
            // Regular cell - add to the appropriate column
            columns[currentCol].push(content);
            // Move to next column or wrap to next row
            currentCol++;
            if (currentCol >= colNumber) {
                currentCol = 0;
                currentRow++;
            }
        }
        return columns;
    }
}
//# sourceMappingURL=PrintableText.js.map